import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ActionsMenu.css';

// Menu d'actions compact « ⋮ » (point 25) — regroupe les actions secondaires d'une ligne de
// tableau derrière un déclencheur à trois points, pour gagner de l'espace horizontal.
// Additif et autonome : n'affecte aucun tableau tant qu'il n'est pas explicitement adopté.
//
// Props :
//   items: [{ label, onClick, icon?, danger?, disabled?, hidden? }]
//   label: aria-label du déclencheur (def. « Actions »)
//   align: 'right' (def.) | 'left' — côté d'ouverture du menu
const ActionsMenu = ({ items = [], label = 'Actions', align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left|right } en coordonnées viewport (position:fixed)
  const ref = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // 🔧 Point — le menu était CLIPPÉ par le conteneur `.table-responsive { overflow:auto }` : un
  // enfant en position:absolute qui déborde sous la dernière ligne est coupé (aucun z-index ne
  // sauve d'un ancêtre clippant). Solution : position:FIXED calculée depuis le rect du déclencheur
  // → le menu sort du flux clippé et flotte au-dessus du tableau.
  const computePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const base = { top: r.bottom + 4 };
    setPos(align === 'left' ? { ...base, left: r.left } : { ...base, right: window.innerWidth - r.right });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;
    computePos();
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    // Recalcule la position si l'utilisateur scrolle/redimensionne pendant que le menu est ouvert.
    const onReflow = () => computePos();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, close, computePos]);

  const visible = items.filter((it) => it && !it.hidden);
  if (visible.length === 0) return null;

  return (
    <div className="am-wrap" ref={ref}>
      <button
        type="button"
        className="am-trigger"
        ref={triggerRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open && pos && (
        <div className="am-menu am-menu--fixed" role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right }}>
          {visible.map((it, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={`am-item${it.danger ? ' am-item--danger' : ''}`}
              disabled={it.disabled}
              onClick={() => { close(); it.onClick?.(); }}
            >
              {it.icon && <span className="am-item__icon">{it.icon}</span>}
              <span className="am-item__label">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionsMenu;
